import { useMemo, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Button as ShadcnButton } from '@/components/shadcn/button';
import type { PadelStandingItem, PadelSessionListItem } from '@/services/padelService';
import type { MabarParticipant } from '@/services/communityService';
import PadelPodium from './PadelPodium';
import PadelStandingRow from './PadelStandingRow';
import mabarService from '@/services/mabarService';
import { downloadOrShareBlob, shareBlob } from '@/utils/downloadBlob';
import { formatDateDDMMYYYY } from '@/utils/dateUtils';
import showToast from '@/components/ui/Toast';
import { RecapCardRenderer, RecapTemplateVariant } from '@/components/ui/recap/RecapCardRenderer';
import { KlasemenStandingItem } from '@/components/ui/recap/KlasemenCard';
import { StatistikExportActions } from '@/components/ui/StatistikExportActions';
import { useAuth } from '@/contexts/AuthContext';
import { getClientProfile, DataProfile } from '@/services/clientProfile';

interface PadelStandingsBoardProps {
  standings: PadelStandingItem[];
  isSparring?: boolean;
  teamAName?: string;
  teamBName?: string;
  teamAColor?: string;
  teamBColor?: string;
  sessions?: PadelSessionListItem[];
  participants?: MabarParticipant[];
  eventName?: string;
  eventDateFormatted?: string;
  location?: string;
  eventSlug?: string;
  userDisplayName?: string;
  userAvatar?: string;
  hideDownload?: boolean;
}

const RACKET_TEMPLATES = [
  {
    id: 'rapor_event_racket',
    title: 'Rapor Event',
    desc: 'Ringkasan Padel & Badminton',
    icon: 'ph:ticket-bold',
  },
  {
    id: 'klasemen',
    title: 'Klasemen',
    desc: 'Tabel & posisi podium akhir',
    icon: 'ph:trophy-bold',
  },
] as const;

export default function PadelStandingsBoard({
  standings,
  isSparring = false,
  teamAName = 'Tim A',
  teamBName = 'Tim B',
  teamAColor,
  teamBColor,
  sessions,
  participants,
  eventName = 'Mabar Padel',
  eventDateFormatted = '',
  location = '',
  eventSlug = 'mabar-event',
  userDisplayName = 'Player',
  userAvatar,
  hideDownload,
}: PadelStandingsBoardProps) {
  const { user } = useAuth();
  const [profile, setProfile] = useState<DataProfile | null>(null);

  useEffect(() => {
    getClientProfile()
      .then((res) => {
        if (res?.success && res.data) {
          setProfile(res.data);
        }
      })
      .catch(() => {});
  }, []);

  const routerLocation = useLocation();
  const isCommunityAdmin = routerLocation.pathname.startsWith('/host');
  const shouldHideDownload = hideDownload || isCommunityAdmin;
  const top = standings.slice(0, 3);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<RecapTemplateVariant>('rapor_event_racket');
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);

  const candidateNames = [
    userDisplayName,
    profile?.name,
    profile?.username,
    user?.name,
    user?.username,
    user?.email ? user.email.split('@')[0] : null,
  ]
    .filter((n): n is string => Boolean(n) && n !== 'Player')
    .map((n) => n.trim().toLowerCase());

  const isMatchUser = (itemName?: string) => {
    if (!itemName) return false;
    const lowerItem = itemName.trim().toLowerCase();
    return candidateNames.some(
      (c) => lowerItem === c || lowerItem.includes(c) || c.includes(lowerItem)
    );
  };

  const currentUserItem = standings.find((s) => isMatchUser(s.playerName)) || null;
  const targetItem = currentUserItem || standings[0];

  const currentDisplayName =
    (currentUserItem ? currentUserItem.playerName : null) ||
    (profile?.name && profile.name !== 'Player' ? profile.name : null) ||
    (user?.name && user.name !== 'Player' ? user.name : null) ||
    (userDisplayName !== 'Player' ? userDisplayName : null) ||
    targetItem?.playerName ||
    'Player';

  const currentAvatar =
    (currentUserItem ? currentUserItem.avatarUrl : null) ||
    profile?.profile_image ||
    user?.avatar ||
    userAvatar;

  const formattedStandings: KlasemenStandingItem[] = standings.map((item, idx) => {
    const hasPoints = (item.pointsScored ?? 0) > 0 || (item.pointsConceded ?? 0) > 0;
    return {
      position: item.position || idx + 1,
      playerName: item.playerName || `Pemain ${idx + 1}`,
      avatarUrl: item.avatarUrl || undefined,
      matches: hasPoints ? item.gamesPlayed || 0 : 0,
      wins: hasPoints ? item.wins || 0 : 0,
      points: item.pointsScored || 0,
      winRate: hasPoints ? Math.round(item.winRate || 0) : 0,
      isCurrentUser: isMatchUser(item.playerName),
    };
  });

  const renderStatistikBlob = async (): Promise<{ blob: Blob; filename: string }> => {
    const hasTargetPoints =
      (targetItem?.pointsScored ?? 0) > 0 || (targetItem?.pointsConceded ?? 0) > 0;
    const blob = await mabarService.renderShareRecapV2(eventSlug, {
      eventName,
      eventDateFormatted: formatDateDDMMYYYY(eventDateFormatted),
      scoreText: 'W',
      winnerName: currentDisplayName,
      stats: {
        goals: 0,
        assists: 0,
        matches: hasTargetPoints ? targetItem?.gamesPlayed || 0 : 0,
        win_rate: hasTargetPoints ? targetItem?.winRate || 0 : 0,
        wins: hasTargetPoints ? targetItem?.wins || 0 : 0,
        point: targetItem?.pointsScored || 0,
        point_loss: targetItem?.pointsConceded || 0,
        is_racket: true,
        rank: targetItem?.position || 1,
      },
      playerName: currentDisplayName,
      playerAvatarUrl: currentAvatar,
      template: selectedTemplate,
      sportCategory: 'Padel',
      location,
      standingsJson: formattedStandings.length > 0 ? JSON.stringify(formattedStandings) : undefined,
    });

    return { blob, filename: `recap-stats-${selectedTemplate}-${eventSlug}.png` };
  };

  const handleDownloadStatistik = async () => {
    try {
      setDownloading(true);
      const { blob, filename } = await renderStatistikBlob();
      const result = await downloadOrShareBlob(blob, filename, { title: eventName });
      if (result === 'saved') {
        showToast.success('Gambar statistik berhasil diunduh/disimpan ke Galeri');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Gagal mengunduh gambar statistik';
      showToast.error(message);
    } finally {
      setDownloading(false);
    }
  };

  const handleShareStatistik = async () => {
    try {
      setSharing(true);
      const { blob, filename } = await renderStatistikBlob();
      const result = await shareBlob(blob, filename, {
        title: eventName,
        dialogTitle: 'Bagikan gambar',
      });
      if (result === 'shared') {
        showToast.success('Gambar statistik berhasil dibagikan');
        setIsShareOpen(false);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Gagal membagikan gambar statistik';
      showToast.error(message);
    } finally {
      setSharing(false);
    }
  };

  const teamSummary = useMemo(() => {
    if (!isSparring) return null;

    let teamAPoints = 0;
    let teamBPoints = 0;
    let teamAWins = 0;
    let teamBWins = 0;

    const allGames = (sessions ?? []).flatMap((s) => s.games ?? []);

    if (allGames.length > 0) {
      allGames.forEach((g) => {
        const scoreA = g.scoreSideA ?? 0;
        const scoreB = g.scoreSideB ?? 0;
        if (g.status === 'completed' || scoreA > 0 || scoreB > 0) {
          teamAPoints += scoreA;
          teamBPoints += scoreB;
          if (scoreA > scoreB || g.winnerSide === 'side_a') {
            teamAWins += 1;
          } else if (scoreB > scoreA || g.winnerSide === 'side_b') {
            teamBWins += 1;
          }
        }
      });
    } else {
      standings.forEach((st) => {
        const part = (participants ?? []).find((p) => p.id === st.participantId);
        const side = (part as any)?.team_side || (part as any)?.teamSide;
        if (side === 'home' || side === 'A' || side === 'side_a') {
          teamAPoints += st.pointsScored;
          teamAWins += st.wins;
        } else if (side === 'away' || side === 'B' || side === 'side_b') {
          teamBPoints += st.pointsScored;
          teamBWins += st.wins;
        }
      });
    }

    let winner: 'A' | 'B' | 'DRAW' = 'DRAW';
    if (teamAWins > teamBWins) winner = 'A';
    else if (teamBWins > teamAWins) winner = 'B';
    else if (teamAPoints > teamBPoints) winner = 'A';
    else if (teamBPoints > teamAPoints) winner = 'B';

    return { teamAPoints, teamBPoints, teamAWins, teamBWins, winner };
  }, [isSparring, sessions, participants, standings]);

  const participantTeamMap = useMemo(() => {
    const map = new Map<number, 'A' | 'B'>();
    if (!isSparring || !participants) return map;
    participants.forEach((p) => {
      const side = (p as any)?.team_side || (p as any)?.teamSide;
      if (side === 'home' || side === 'A' || side === 'side_a') {
        map.set(p.id, 'A');
      } else if (side === 'away' || side === 'B' || side === 'side_b') {
        map.set(p.id, 'B');
      }
    });
    return map;
  }, [isSparring, participants]);

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-xl border border-blue-100 bg-[#EFF6FF] p-4">
        <Icon
          icon="mdi:information-outline"
          className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#1E66F5]"
        />
        <p className="text-xs leading-relaxed text-[#1E66F5]">
          Klasemen terakumulasi dari <span className="font-semibold">semua sesi</span> dalam event
          ini. Update otomatis setiap skor diinput.
        </p>
      </div>

      {!shouldHideDownload && (
        <div className="flex">
          <ShadcnButton
            type="button"
            onClick={() => setIsShareOpen(true)}
            className="w-full gap-2 bg-[#FC480D] hover:bg-[#D83B08] text-white font-semibold rounded-xl h-11 cursor-pointer transition-all shadow-sm"
          >
            <Icon icon="ph:download-simple-bold" className="h-4.5 w-4.5" />
            <span>Download Statistik</span>
          </ShadcnButton>
        </div>
      )}

      {isSparring && teamSummary && (
        <div className="space-y-2">
          <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Rekap Tim</div>
          <div className="rounded-2xl bg-[#1C2733] overflow-hidden">
            <div className="grid grid-cols-[1fr_auto_1fr] items-center px-6 py-6 sm:px-8">
              <div className="text-right pr-4 sm:pr-6">
                <div className="flex items-center justify-end gap-2 mb-1">
                  {teamSummary.winner === 'A' && (
                    <span className="bg-[#10B981]/20 text-[#10B981] text-[10px] font-bold px-2 py-0.5 rounded-md border border-[#10B981]/30">
                      MENANG
                    </span>
                  )}
                  <div
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: teamAColor || '#3B82F6' }}
                  />
                  <span className="font-bold text-white text-base sm:text-lg tracking-tight truncate max-w-[120px] sm:max-w-[160px]">
                    {teamAName}
                  </span>
                </div>
                <div className="text-3xl sm:text-4xl font-black text-white tracking-tight">
                  {teamSummary.teamAWins}{' '}
                  <span className="text-xs font-normal text-gray-400">menang</span>
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {teamSummary.teamAPoints} Total Poin
                </div>
              </div>

              <div className="px-3 sm:px-5 py-2 text-center border-x border-gray-700/50">
                <div className="text-[10px] font-extrabold tracking-widest text-gray-400 uppercase">
                  VS
                </div>
              </div>

              <div className="text-left pl-4 sm:pl-6">
                <div className="flex items-center justify-start gap-2 mb-1">
                  <span className="font-bold text-white text-base sm:text-lg tracking-tight truncate max-w-[120px] sm:max-w-[160px]">
                    {teamBName}
                  </span>
                  <div
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: teamBColor || '#EF4444' }}
                  />
                  {teamSummary.winner === 'B' && (
                    <span className="bg-[#10B981]/20 text-[#10B981] text-[10px] font-bold px-2 py-0.5 rounded-md border border-[#10B981]/30">
                      MENANG
                    </span>
                  )}
                </div>
                <div className="text-3xl sm:text-4xl font-black text-white tracking-tight">
                  {teamSummary.teamBWins}{' '}
                  <span className="text-xs font-normal text-gray-400">menang</span>
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {teamSummary.teamBPoints} Total Poin
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {standings.length === 0 ? (
        <div className="rounded-xl border bg-white p-8 text-center text-gray-500">
          Belum ada data klasemen
        </div>
      ) : (
        <div className="rounded-xl border bg-white p-4">
          <div className="mb-4 flex items-center gap-2">
            <Icon icon="mdi:trophy" className="h-5 w-5 text-[#FBBF24]" />
            <h3 className="text-base font-bold text-gray-900">Ranking Pemain</h3>
          </div>

          <div className="mb-2">
            <PadelPodium top={top} />
          </div>

          <div>
            {standings.map((item) => (
              <PadelStandingRow
                key={item.participantId}
                item={item}
                teamName={participantTeamMap.get(item.participantId)}
                teamAName={teamAName}
                teamBName={teamBName}
                teamAColor={teamAColor}
                teamBColor={teamBColor}
              />
            ))}
          </div>
        </div>
      )}

      {/* Share / Download Bottom Sheet Modal */}
      {isShareOpen && (
        <div
          className="fixed inset-0 z-50 bg-[#0E1726]/60 backdrop-blur-xs flex items-end justify-center animate-in fade-in duration-200"
          style={{ marginTop: 0, paddingTop: 'calc(1rem * calc(1 - var(--tw-space-y-reverse, 0)))' }}
          onClick={() => setIsShareOpen(false)}
        >
          <div
            className="w-full max-w-md bg-white rounded-t-3xl p-5 pb-6 flex flex-col gap-4 shadow-2xl animate-in slide-in-from-bottom duration-300 max-h-[92vh] overflow-y-auto text-left"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle bar */}
            <div className="w-12 h-1.5 rounded-full bg-gray-300 self-center shrink-0" />

            {/* Header info */}
            <div className="flex flex-col gap-0.5">
              <h4 className="text-lg font-bold text-gray-900">Bagikan statistik</h4>
              <span className="text-xs text-gray-500 truncate">
                {eventName} {eventDateFormatted ? `· ${formatDateDDMMYYYY(eventDateFormatted)}` : ''}
              </span>
            </div>

            {/* Live Preview Container (540x960 scaled down to 270x480) */}
            <div className="w-[270px] h-[480px] rounded-2xl overflow-hidden shadow-lg border border-gray-200 relative shrink-0 mx-auto bg-[#0E1726] flex items-center justify-center">
              <div
                className="w-[540px] h-[960px] absolute top-0 left-0 origin-top-left"
                style={{ transform: 'scale(0.5)' }}
              >
                <RecapCardRenderer
                  template={selectedTemplate}
                  eventName={eventName}
                  eventDateFormatted={formatDateDDMMYYYY(eventDateFormatted)}
                  playerName={currentUserItem?.playerName || userDisplayName}
                  playerAvatarUrl={currentUserItem?.avatarUrl || userAvatar}
                  rank={currentUserItem?.position || 1}
                  stats={{
                    goals: 0,
                    assists: 0,
                    matches:
                      (currentUserItem?.pointsScored ?? 0) > 0 || (currentUserItem?.pointsConceded ?? 0) > 0
                        ? currentUserItem?.gamesPlayed || 0
                        : 0,
                    wins:
                      (currentUserItem?.pointsScored ?? 0) > 0 || (currentUserItem?.pointsConceded ?? 0) > 0
                        ? currentUserItem?.wins || 0
                        : 0,
                    win_rate:
                      (currentUserItem?.pointsScored ?? 0) > 0 || (currentUserItem?.pointsConceded ?? 0) > 0
                        ? currentUserItem?.winRate || 0
                        : 0,
                    point: currentUserItem?.pointsScored || 0,
                    point_loss: currentUserItem?.pointsConceded || 0,
                    is_racket: true,
                    rank: currentUserItem?.position || 1,
                  }}
                  standings={formattedStandings}
                  totalPlayers={formattedStandings.length || undefined}
                  sportCategory="Padel"
                  location={location}
                  transparent={false}
                />
              </div>
            </div>

            {/* Template Selector Grid */}
            <div className="grid grid-cols-2 gap-2.5 pt-1">
              {RACKET_TEMPLATES.map((tmpl) => {
                const isActive = selectedTemplate === tmpl.id;
                return (
                  <button
                    key={tmpl.id}
                    type="button"
                    onClick={() => setSelectedTemplate(tmpl.id)}
                    className={`flex flex-col items-center justify-between p-3 rounded-2xl text-center transition-all cursor-pointer min-h-[96px] ${
                      isActive
                        ? 'border-2 border-[#FC480D] bg-[#FFF4EE]'
                        : 'border border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <Icon
                      icon={tmpl.icon}
                      className={`w-6 h-6 mb-1 shrink-0 ${
                        isActive ? 'text-[#FC480D]' : 'text-gray-700'
                      }`}
                    />
                    <div className="flex flex-col gap-0.5 min-w-0 w-full">
                      <span
                        className={`text-xs truncate block ${
                          isActive ? 'font-bold text-[#FC480D]' : 'font-semibold text-gray-900'
                        }`}
                      >
                        {tmpl.title}
                      </span>
                      <span className="text-[9px] text-gray-400 leading-tight line-clamp-2 block font-normal">
                        {tmpl.desc}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Main Action Buttons */}
            <StatistikExportActions
              busy={downloading ? 'download' : sharing ? 'share' : null}
              onDownload={() => void handleDownloadStatistik()}
              onShare={() => void handleShareStatistik()}
              onClose={() => setIsShareOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
